import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  VpnKey as KeyIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import ConfirmDialog from './ConfirmDialog';
import ApiKeyCreateDialog from './ApiKeyCreateDialog';
import ApiKeyRevealDialog from './ApiKeyRevealDialog';
import { copyToClipboard } from '../utils/clipboard';

const fmtDate = (d) => {
  if (!d) return 'Never';
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const STATUS_COLOR = { active: 'success', expired: 'warning', revoked: 'default' };

/**
 * "API Keys" on the profile page (#1115) — create a named, revocable credential to use instead of
 * putting your password in a script.
 *
 * The list shows metadata only. That is not a policy choice enforced here: the server stores an
 * HMAC of the secret and has no endpoint that could return it, so there is nothing to leak.
 */
const ApiKeysSection = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getMyApiKeys();
      setKeys(res?.data?.api_keys || []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (payload) => {
    const res = await apiService.createMyApiKey(payload);
    setCreateOpen(false);
    // The secret exists only in this response. It goes straight to the reveal dialog and is never
    // persisted anywhere in the browser.
    setRevealed(res?.data || null);
    load();
  };

  const handleRevoke = async () => {
    try {
      await apiService.revokeMyApiKey(revokeTarget.id);
      setRevokeTarget(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to revoke API key');
      throw e;   // ConfirmDialog stays open when onConfirm rejects
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon />
          API Keys
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Add key
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use an API key instead of your password for scripts and integrations. Present it as HTTP
        Basic auth — <code>curl -u &lt;key id&gt;:&lt;secret&gt;</code>. A key acts with your
        permissions, but cannot sign in, change your password, or manage API keys.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
      ) : keys.length === 0 ? (
        // Only claim there are none when the list actually loaded — after a failure we do not know.
        !error && (
          <Typography variant="body2" color="text.secondary">
            No API keys yet.
          </Typography>
        )
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {keys.map((k) => (
            <Box
              key={k.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1,
                opacity: k.status === 'active' ? 1 : 0.6,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{k.name}</Typography>
                  <Chip size="small" label={k.status} color={STATUS_COLOR[k.status] || 'default'}
                    sx={{ height: 18, fontSize: '0.65rem' }} />
                  {k.read_only && (
                    <Chip size="small" variant="outlined" label="read-only"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{k.key_id}</Typography>
                  <Tooltip title="Copy key id">
                    <IconButton size="small" aria-label={`Copy key id for ${k.name}`}
                      onClick={() => copyToClipboard(k.key_id)}>
                      <CopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Created {fmtDate(k.created_at)} · Last used {fmtDate(k.last_used_at)} · Expires {fmtDate(k.expires_at)}
                </Typography>
              </Box>
              {k.status !== 'revoked' && (
                <Tooltip title="Revoke">
                  <IconButton size="small" aria-label={`Revoke ${k.name}`} onClick={() => setRevokeTarget(k)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}

      <ApiKeyCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      <ApiKeyRevealDialog
        open={!!revealed}
        apiKey={revealed}
        onClose={() => setRevealed(null)}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke API key?"
        message={revokeTarget
          ? `"${revokeTarget.name}" will stop working immediately. Any application using it will start failing. This cannot be undone.`
          : ''}
        confirmLabel="Revoke"
        confirmColor="error"
        onConfirm={handleRevoke}
        onClose={() => setRevokeTarget(null)}
      />
    </Paper>
  );
};

export default ApiKeysSection;
